import { useState, ImgHTMLAttributes } from 'react';
import { useSignedUrl } from '@/hooks/useSignedUrl';
import { Skeleton } from './skeleton';

type BucketName = 'patient-photos' | 'shift-photos' | 'avatars' | 'company-logos';

interface SignedImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | null | undefined;
  bucket: BucketName;
  fallback?: React.ReactNode;
  showSkeleton?: boolean;
}

/**
 * Image component that automatically fetches signed URLs for private storage buckets
 * Uses useSignedUrl hook with built-in caching and request deduplication
 */
export function SignedImage({
  src,
  bucket,
  fallback = null,
  showSkeleton = true,
  className,
  alt = '',
  ...props
}: SignedImageProps) {
  const signedUrl = useSignedUrl(src, bucket);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [error, setError] = useState(false);

  if (!src) {
    return <>{fallback}</>;
  }

  // Still loading the signed URL
  if (!signedUrl && showSkeleton) {
    return <Skeleton className={className} />;
  }

  if (error || !signedUrl) {
    return <>{fallback}</>;
  }

  return (
    <>
      {!imageLoaded && showSkeleton && <Skeleton className={className} />}
      <img
        src={signedUrl}
        alt={alt}
        className={className}
        onLoad={() => setImageLoaded(true)}
        onError={() => setError(true)}
        style={!imageLoaded ? { display: 'none' } : undefined}
        {...props}
      />
    </>
  );
}

